/**
 * Which report views a profile may open, and which one is its own (DEC-190 decision 7).
 *
 * The same rule as `RequireProfile` in App.tsx and the backend's `require_profile`
 * (DEC-085, DEC-179): a profile opens its own view; `admin` and a token with no profile
 * open every view. Before DEC-190 every report link went to the Engineering view, so an
 * Executive or Product user clicking "Report" landed on "Not available for your profile"
 * with no way back. Every report link in the app is built from here, so none can point
 * at a view the reader cannot open.
 */
import type { Profile } from '../../types/api'

export type ReportViewId = 'engineering' | 'executive' | 'product'

export interface ReportView {
  id: ReportViewId
  label: string
  path: (scanId: string) => string
}

export const REPORT_VIEWS: ReportView[] = [
  { id: 'engineering', label: 'Engineering view', path: (id) => `/scans/${id}/findings` },
  { id: 'executive', label: 'Executive view', path: (id) => `/scans/${id}/executive-report` },
  { id: 'product', label: 'Product & GTM view', path: (id) => `/scans/${id}/product-report` },
]

const OWN_VIEW: Record<Exclude<Profile, 'admin'>, ReportViewId> = {
  engineering: 'engineering',
  executive: 'executive',
  product_gtm: 'product',
}

/** The views this profile may open, in display order. */
export function reportViewsFor(profile: Profile | null): ReportView[] {
  if (profile === null || profile === 'admin') return REPORT_VIEWS
  return REPORT_VIEWS.filter((v) => v.id === OWN_VIEW[profile])
}

/** The view a report link opens by default: the reader's own, or Engineering for an
 *  admin or a token with no profile. */
export function ownReportView(profile: Profile | null): ReportView {
  return reportViewsFor(profile)[0]
}

/** Where "Open results" goes for this reader. */
export function resultsPath(profile: Profile | null, scanId: string): string {
  return ownReportView(profile).path(scanId)
}
